<template>
  <v-layout
    row
    wrap
  >
    <v-flex 
      v-if="componentLoading"    
      xs12
      d-flex
      justify-center
    >
      <v-progress-circular
        :size="64"
        :width="3"
        indeterminate
        color="primary"
      />
    </v-flex>
    <v-flex 
      v-else      
      xs12
    >
      <v-card
        id="visitorsTableContent"
        class="max-shadow"
      >
        <v-toolbar
          flat
          color="white"
          height="48"
        >
          <v-spacer />
          <v-toolbar-items>
            <search-widget
              v-model="search" 
            />
            <v-btn
              flat
              large
              color="primary"
              @click="exportVisitors"
            >
              Export
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-flex xs12>
          <v-data-table
            id="visitorsTable"
            :headers="visitorsHeaders"
            :items="visitors"
            :search="search"
            :loading="progressbar"
            :rows-per-page-items="[10,25,50]"
            :pagination.sync="pagination"
            item-key="id"
            no-data-text="No visitor is added"
            class="px-2 pb-2"
            disable-initial-sort
          >
            <v-progress-linear
              slot="progress"
              color="primary"
              indeterminate
            />
            <template
              slot="items"
              slot-scope="props"
            >
              <tr
                class=" row-border"
                @click="$router.push('/max-admin/vms/visitors/' + props.item.id)"
              >
                <td
                  class="tablePadding"
                  style="width: 20px"
                >
                  {{ getIndex(props.index + 1) }}
                </td>
                <td
                  class="text-xs-left tablePadding"
                  style="width: 40px"
                >
                  <avatar
                    :size="40"
                    :src="props.item.photoUri"
                    :text="props.item.name"
                  />
                </td>
                <td class="text-xs-left tablePadding">
                  {{ props.item.name }}
                </td>
                <td class="text-xs-left tablePadding">
                  {{ props.item.email }}
                </td>
                <td class="text-xs-left tablePadding">
                  {{ props.item.phone }}
                </td>
                <td class="text-xs-left tablePadding">
                  {{ props.item.company }}
                </td>
                <td
                  class="justify-end layout px-0"
                  style="width: 85px"
                >
                  <v-tooltip
                    :open-delay="1500"
                    bottom
                  >
                    <v-btn
                      slot="activator"
                      icon
                      class="mx-0"
                      @click.stop="editVisitor(props.item)"
                    >
                      <v-icon color="iconColor">
                        edit
                      </v-icon>
                    </v-btn>
                    <span>Edit</span>
                  </v-tooltip>
                  <v-tooltip
                    :open-delay="1500"
                    bottom
                  >
                    <v-btn
                      slot="activator"
                      icon
                      class="mx-0"
                      @click.stop="deleteVisitor(props.item)"
                    >
                      <v-icon color="iconColor">
                        delete
                      </v-icon>
                    </v-btn>
                    <span>Delete</span>
                  </v-tooltip>
                </td>
              </tr>
            </template>
            <template slot="no-results">
              <p class="text-xs-center">
                Your search for <span class="font-weight-bold subheading">
                  {{ search }}
                </span> found no results.
              </p>
            </template>
          </v-data-table>
        </v-flex>
      </v-card>
      <v-dialog
        v-model="editVisitorDialog"
        lazy
        max-width="600px"
      >
        <edit-visitor
          :visitor="selectedVisitor"
          @update="editVisitorDialog = false"
          @cancel="editVisitorDialog = false"
        />
      </v-dialog>
      <v-dialog
        v-model="deleteVisitorDialog"
        lazy
        max-width="600px"
      >
        <v-card class="max-shadow">
          <v-card-title primary-title>
            <div class="headline">
              Delete {{ selectedVisitor.name }}
            </div>
          </v-card-title>
          <v-card-text>
            <div class="subheading">
              Do you want to delete visitor
              <strong>{{ selectedVisitor.name }}</strong>?
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              flat
              color="primary"
              @click.stop="deleteVisitorDialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              flat
              color="primary"
              @click.stop="confirmDeleteVisitor"
            >
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-snackbar
        v-model="snackbar"
        :timeout="0"
        :color="snackbarColor"
        absolute
        multi-line
        bottom
      >
        {{ snackbarMessage }}
        <v-btn
          flat
          @click.native="snackbar = false"
        >
          Close
        </v-btn>
      </v-snackbar>
    </v-flex>
  </v-layout>
</template>

<script>
import { database } from '@/firebase';
import * as moment from 'moment';
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import Papa from 'papaparse';

import validationRules from '@/mixins/validation-rules';

import avatar from '@/components/common/Avatar';
import SearchWidget from '@/components/common/SearchWidget';
import EditVisitor from './EditVisitor';

export default {
  name: 'VisitorsTable',
  components: {
    avatar,
    EditVisitor,
    'search-widget': SearchWidget
  },
  mixins: [validationRules],
  props: {
    size: {
      type: Number,
      default: 48
    }
  },
  data() {
    return {
      progressbar: true,
      visitors: [],
      visitorsHeaders: [
        {
          text: '#',
          value: false,
          align: 'left',
          width: '10px',
          class: ['tablePadding']
        },
        {
          text: 'Photo',
          value: 'photo',
          align: 'left',
          class: ['tablePadding']
        },
        {
          text: 'Name',
          value: 'name',
          align: 'left',
          class: ['tablePadding']
        },
        {
          text: 'Email',
          value: 'email',
          align: 'left',
          class: ['tablePadding']
        },
        {
          text: 'Phone',
          value: 'phone',
          align: 'left',
          class: ['tablePadding']
        },
        {
          text: 'Company',
          value: 'company',
          align: 'left',
          class: ['tablePadding']
        },
        {
          text: 'Actions',
          value: 'name',
          sortable: false
        }
      ],
      search: '',
      pagination: {
        rowsPerPage: 10
      },
      firebaseListeners: [],
      selectedVisitor: {},
      editVisitorDialog: false,
      deleteVisitorDialog: false,
      snackbar: false,
      snackbarColor: 'primary',
      snackbarMessage: '',

      componentLoading: true
    };
  },
  computed: {
    visitorsRef() {
      const lid = _get(this.$store.state.VMS, 'lid');
      // return null if the location ID is not yet set
      if (!lid) return null;

      return database.ref(`vms/${lid}/visitors`);
    }
  },
  watch: {
    visitorsRef: {
      handler(val) {
        if (!val) return;

        this.firebaseListeners.forEach(listenerFunc => {
          val.off('value', listenerFunc);
        });

        this.fetchVisitors();
      },
      immediate: true
    }
  },
  beforeDestroy() {
    // unsubscribe all firebase listeners
    this.firebaseListeners.forEach(listenerFunc => {
      this.visitorsRef.off('value', listenerFunc);
    });
  },
  methods: {
    // TODO: implement firebase side pagenation
    fetchVisitors() {
      const visitorQuery = this.visitorsRef.on('value', visitorsSnapshot => {
        if (visitorsSnapshot) {
          this.componentLoading = false;
          const visitors = visitorsSnapshot.val();
          const visitorsArray = this._toArray(visitors);
          this.visitors = visitorsArray;
          this.progressbar = false;
        }
      });
      this.firebaseListeners.push(visitorQuery);
    },

    formatTime(time) {
      return moment(time).format('DD MMM YYYY hh:mm A');
    },

    _toArray(obj) {
      if (!obj) return [];
      return Object.keys(obj).map(key => {
        const item = obj[key];
        item.id = key;
        return item;
      });
    },

    exportVisitors() {
      const headers = [
        'Full Name',
        'Phone Number',
        'Email',
        'Company',
        'Photo URL'
      ];
      const dataArr = this.visitors.map(vis => [
        vis.name,
        vis.phone,
        vis.email,
        vis.company,
        vis.photoUri
      ]);

      const config = {
        quotes: true,
        quoteChar: '"',
        delimiter: ',',
        header: true,
        newline: '\r\n'
      };
      const csvData = {
        fields: headers,
        data: dataArr
      };
      const csv = Papa.unparse(csvData, config);

      const blob = new Blob([csv], {
        type: 'text/csv;charset=utf-8;'
      });

      const filename = `visitors_export_${moment().format('DD_MM_YYYY')}.csv`;
      if (navigator.msSaveBlob) {
        // IE 10+
        navigator.msSaveBlob(blob, filename);
      } else {
        const link = document.createElement('a');
        // feature detection
        if (link.download !== undefined) {
          // Browsers that support HTML5 download attribute
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', filename);
          link.style.visibility = 'hidden';

          link.click();
        }
      }
    },

    getIndex(index) {
      const { page, rowsPerPage } = this.pagination;
      return index + (page - 1) * rowsPerPage;
    },

    editVisitor(visitor) {
      this.selectedVisitor = _cloneDeep(visitor);
      this.editVisitorDialog = true;
    },

    deleteVisitor(visitor) {
      this.selectedVisitor = _cloneDeep(visitor);
      this.deleteVisitorDialog = true;
    },

    confirmDeleteVisitor() {
      this.visitorsRef
        .child(`${this.selectedVisitor.id}`)
        .set(null)
        .catch(() => {
          this.snackbarColor = 'Error';
          this.snackbarMessage =
            'Error deleting visitor! Please try after some time';
          this.snackbar = true;
        });
      this.deleteVisitorDialog = false;
    }
  }
};
</script>

<style>
.wrapper {
  text-align: center;
}
#visitorsTableContent {
  height: calc(100vh - var(--toolbar-height));
}
#visitorsTable .v-table__overflow {
  height: calc(100vh - var(--pagination-height));
  overflow-y: scroll;
}
</style>

